import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouteObject,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { AuthContext, ThemeContext } from 'alurkerja-ui';
import moment from 'moment';
import 'moment/dist/locale/id';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { axiosInstance } from './utils/lib/axios';
import * as Sentry from '@sentry/react';
import { routes } from './routes/root';
import { theme } from './utils/configs/theme';
import { env } from './utils/constants/env';

//  CSS
import './assets/index.css';
import 'alurkerja-ui/dist/style.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { KeycloakProvider } from 'keycloak-react-web';
import Keycloak from 'keycloak-js';

// moment
moment.locale('id');

// react-query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// route
const createRouter = (routes: RouteObject[]) => createBrowserRouter(routes);
const router = createRouter(routes);

// sentry
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
      traceFetch: true,
      traceXHR: true,
    }),
  ],
  tracesSampleRate: 1.0,
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  tracePropagationTargets: [
    env.apiBaseUrl,
    env.apiTasklistBaseUrl
  ]
});

// keycloak
const keycloakInstance = new Keycloak({
  url: env.keycloakUrl,
  realm: env.keycloakRealm,
  clientId: env.keycloakClientId,
});

// initial render
const root = document.getElementById('root');
if (!root) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(root).render(
  // @ts-expect-error invalid type
  <KeycloakProvider client={keycloakInstance} initOptions={{ onLoad: 'login-required', checkLoginIframe: false }}>
    <QueryClientProvider client={queryClient}>
      <AuthContext.Provider value={axiosInstance}>
        <ThemeContext.Provider value={theme}>
          <RouterProvider router={router} />
        </ThemeContext.Provider>
        <ReactQueryDevtools initialIsOpen={env.queryDebug} />
      </AuthContext.Provider>
    </QueryClientProvider>
  </KeycloakProvider>,
);
